// Point the device behind a unique number at a firmware file (.ino.bin) —
// inline, for the Message Box (both shells), beside DeviceTypePicker.
//
// Writes DevicesInstalled.OTABinFileId — the PER-DEVICE override of the type's
// image, the only thing that can express a mixed-chip fleet — through the same
// core/devices/otaBinFiles.mapDeviceToBinFile the "Set a device's firmware file"
// dialog uses, so the two cannot disagree.
//
// ⚠️ THE READ-BACK IS THE POINT. After writing it asks GetDeviceOTAUrlLink — the
// call the FIRMWARE makes — and shows what the device will now actually
// download. Everything here fails by reporting success.
//
// ⚠️ A WRONG-CHIP IMAGE ALSO REPORTS SUCCESS (the device stamps CodeUpdated
// before flashing). ESP8266 / ESP32-C2 (8684) / ESP32-C3 (8685) images are not
// interchangeable — trust the image header, never the filename.

import React, { useEffect, useState } from 'react';
import {
  Box, TextField, MenuItem, Button, Typography, CircularProgress,
} from '@mui/material';
import { getOtaBinFiles } from '../../core/api/api';
import { mapDeviceToBinFile } from '../../core/devices/otaBinFiles';

let binsPromise = null;   // one list per session; it changes only on a publish
const loadBins = () => {
  if (!binsPromise) {
    binsPromise = getOtaBinFiles()
      // A row with an empty BinFileName would read as blank and un-point the device.
      .then((res) => (Array.isArray(res.data) ? res.data : [])
        .filter((r) => String(r.BinFileName || '').trim())
        .sort((a, b) => String(a.BinFileName).localeCompare(String(b.BinFileName))))
      .catch(() => { binsPromise = null; return []; });
  }
  return binsPromise;
};

export default function DeviceFirmwarePicker({ device, onSaved, dense = false }) {
  const uno = device && device.UniqueNumber;
  const current = device && device.OTABinFileId != null ? String(device.OTABinFileId) : '';
  const [bins, setBins] = useState([]);
  const [pick, setPick] = useState(current);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);   // { ok, text }

  useEffect(() => { loadBins().then(setBins); }, []);
  useEffect(() => { setPick(current); setNote(null); }, [uno, current]);

  const changed = !!pick && pick !== current;

  const save = async () => {
    if (!changed || busy) return;
    setBusy(true);
    setNote(null);
    try {
      const row = bins.find((b) => String(b.OTABinFileId) === String(pick));
      const out = await mapDeviceToBinFile(uno, pick, row && row.BinFileName);
      setNote(out.ok
        ? { ok: true, text: `Saved — the device will now download ${out.url}` }
        : { ok: false, text: `Saved, but the server still serves: ${out.url || '(nothing)'}` });
      if (onSaved) onSaved();
    } catch (e) {
      setNote({ ok: false, text: 'The firmware file could not be saved.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexDirection: dense ? 'column' : 'row' }}>
        <TextField
          select size="small" fullWidth label="Firmware (.ino.bin)"
          value={bins.some((b) => String(b.OTABinFileId) === pick) ? pick : ''}
          onChange={(e) => { setPick(String(e.target.value)); setNote(null); }}
          disabled={!uno || busy || !bins.length}
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
          inputProps={{ style: { fontSize: 12.5 } }}
          helperText={!current ? 'No per-device file — the device type\'s image is used.' : undefined}
        >
          {bins.map((b) => (
            <MenuItem key={b.OTABinFileId} value={String(b.OTABinFileId)} sx={{ fontSize: 12.5, fontFamily: 'monospace' }}>
              {b.BinFileName}
              <Typography component="span" sx={{ ml: 1, fontSize: 11, opacity: 0.6 }}>{`#${b.OTABinFileId}`}</Typography>
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small" variant="contained" color="warning" onClick={save} disabled={!changed || busy}
          sx={{ textTransform: 'none', minWidth: 72, alignSelf: dense ? 'stretch' : 'auto' }}
        >
          {busy ? <CircularProgress size={16} /> : 'Set'}
        </Button>
      </Box>
      {changed && !note && (
        <Typography sx={{ fontSize: 11, mt: 0.25, color: 'warning.main' }}>
          Pick the image for THIS board&apos;s chip (ESP8266 / ESP32-C2 8684 / ESP32-C3 8685) — a
          wrong-chip image still reports success. Nothing flashes until Update Code.
        </Typography>
      )}
      {note && (
        <Typography sx={{ fontSize: 11.5, mt: 0.25, color: note.ok ? 'success.main' : 'error.main', wordBreak: 'break-all' }}>
          {note.text}
        </Typography>
      )}
    </Box>
  );
}
